import { useCallback, useEffect, useRef } from "react";
import {
  activeConversationExitGuard,
  confirmedCommercialProjectionSnapshot,
  confirmedNextContactSnapshot,
  hasRealFollowUpRecord,
} from "./futureFollowUpGuard.mjs";
import { reportNextContactBlock, retryNextContactSave } from "./nextContactExitSave.mjs";

export default function useFutureFollowUpExit(record, owner, onClose, options = {}) {
  const latest = useRef({ record, options });
  latest.current = { record, options };
  const token = useRef(null);
  const tokenOwner = useRef(null);
  const activeOwner = hasRealFollowUpRecord(record) ? owner : null;
  const initialConfirmed = useRef({ owner:null, snapshot:null });
  if (initialConfirmed.current.owner !== activeOwner) {
    initialConfirmed.current = {
      owner:activeOwner,
      snapshot:activeOwner ? confirmedNextContactSnapshot(record) : null,
    };
  }
  const initialConfirmedSnapshot = initialConfirmed.current.snapshot;
  const openingGeneration = initialConfirmed.current;
  const commercialProjectionSnapshot = confirmedCommercialProjectionSnapshot(record);
  const commercialProjectionFingerprint = commercialProjectionSnapshot
    ? JSON.stringify(commercialProjectionSnapshot)
    : "";

  useEffect(() => {
    if (!activeOwner) return undefined;
    // The visible input authorizes date-based exits. Opening snapshots still
    // fence terminal-stage exemptions and callbacks from previous records.
    const registeredToken = activeConversationExitGuard.register(activeOwner, initialConfirmedSnapshot, {
      isExempt:() => latest.current.options.isExempt?.(latest.current.record) === true,
      automaticExit:options.automaticExit === true,
      getDate:latest.current.options.getDate ? () => latest.current.options.getDate() : undefined,
      save:latest.current.options.onSave ? (date, context) => retryNextContactSave(
        (value, metadata) => {
          if (initialConfirmed.current !== openingGeneration) throw new Error("next_contact_stale");
          return latest.current.options.onSave(value, metadata);
        }, date, context,
      ) : null,
      report:reason => reportNextContactBlock({
        storeId:Number(String(activeOwner).split(":")[1]) || latest.current.record?.lojaAtual || latest.current.record?.storeId,
        screen:latest.current.options.screen || (String(activeOwner).startsWith("lead:") ? "lead"
          : String(activeOwner).startsWith("prosp:") ? "prospeccao" : "central"),
      }, reason),
    });
    token.current = registeredToken;
    tokenOwner.current = activeOwner;
    return () => {
      activeConversationExitGuard.unregister(registeredToken);
      if (token.current === registeredToken) {
        token.current = null;
        tokenOwner.current = null;
      }
    };
  }, [activeOwner, initialConfirmedSnapshot, openingGeneration, options.automaticExit]);

  useEffect(() => {
    if (!activeOwner || !token.current || !commercialProjectionSnapshot) return;
    if (initialConfirmedSnapshot?.disqualified || initialConfirmedSnapshot?.noResponse
      || initialConfirmedSnapshot?.referral) return;
    activeConversationExitGuard.updatePersistedDate(token.current, commercialProjectionSnapshot, { saved:false });
  }, [activeOwner, initialConfirmedSnapshot, commercialProjectionFingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  const close = useCallback((afterClose) => {
    if (initialConfirmed.current !== openingGeneration) return false;
    if (activeOwner && (!token.current || tokenOwner.current !== activeOwner)) return false;
    return activeConversationExitGuard.requestAction(() => {
      if (initialConfirmed.current !== openingGeneration) return;
      if (token.current) activeConversationExitGuard.unregister(token.current);
      token.current = null;
      tokenOwner.current = null;
      onClose();
      if (typeof afterClose === "function") afterClose();
    }, token.current || undefined);
  }, [activeOwner, openingGeneration, onClose]);

  const dismiss = useCallback(() => {
    if (initialConfirmed.current !== openingGeneration) return false;
    if (activeOwner && tokenOwner.current !== activeOwner) return false;
    if (token.current) activeConversationExitGuard.unregister(token.current);
    token.current = null;
    tokenOwner.current = null;
    onClose();
  }, [activeOwner, openingGeneration, onClose]);

  const saved = useCallback((savedRecord) => {
    if (initialConfirmed.current !== openingGeneration) return false;
    if (!token.current || tokenOwner.current !== activeOwner) return false;
    return activeConversationExitGuard.updatePersistedDate(token.current, savedRecord);
  }, [activeOwner, openingGeneration]);

  const canExit = useCallback(() => !activeOwner
    || (initialConfirmed.current === openingGeneration && tokenOwner.current === activeOwner && !!token.current
      && activeConversationExitGuard.canExit(token.current)), [activeOwner, openingGeneration]);

  return { close, dismiss, saved, canExit };
}